import { createContext, PropsWithChildren, ReactNode, useCallback, useContext } from "react"

import { useAppDispatch, useAppSelector } from "store/hooks"
import { mobileMenuOpenSelector, setVerticalMenuOpenMobile, verticalMenuOpenSelector } from "store/tempDataSlise"

import { useWindowResize } from "hooks/useWindowResize"

import {
  MenuIcon,
  MenuIconStroke,
  MenuItems,
  MenuText,
  MobileOverlay,
  RedDot,
  StyledNav,
  StyledNavLink,
} from "./styled"

const VerticalMenuContext = createContext<{ isOpen: boolean; closeMobileMenu: () => void }>({
  isOpen: false,
  closeMobileMenu: () => {},
})

export const useVerticalMenuContext = () => useContext(VerticalMenuContext)

export const VerticalMenu = ({ children }: PropsWithChildren) => {
  const dispatch = useAppDispatch()
  const desktopMenuOpen = useAppSelector(verticalMenuOpenSelector)
  const mobileMenuOpen = useAppSelector(mobileMenuOpenSelector)
  const isLargeScreen = useWindowResize()

  const isOpen = isLargeScreen ? desktopMenuOpen : mobileMenuOpen

  const closeMobileMenu = useCallback(() => {
    if (!isLargeScreen) {
      dispatch(setVerticalMenuOpenMobile(false))
    }
  }, [dispatch, isLargeScreen])

  return (
    <VerticalMenuContext.Provider value={{ isOpen, closeMobileMenu }}>
      <MobileOverlay $isOpen={mobileMenuOpen} onClick={closeMobileMenu} />
      <StyledNav $isOpen={isOpen}>
        <MenuItems>{children}</MenuItems>
      </StyledNav>
    </VerticalMenuContext.Provider>
  )
}

interface MenuItemProps {
  to: string
  icon: ReactNode
  label: string
  badge?: ReactNode
  iconType?: "fill" | "stroke"
}

export const MenuItem = ({ to, icon, label, badge, iconType = "fill" }: MenuItemProps) => {
  const { isOpen, closeMobileMenu } = useVerticalMenuContext()
  const IconWrapper = iconType === "stroke" ? MenuIconStroke : MenuIcon

  const handleClick = () => {
    window.scrollTo({ top: 0 })
    closeMobileMenu()
  }

  return (
    <StyledNavLink to={to} activeClassName="active" onClick={handleClick}>
      <IconWrapper>{icon}</IconWrapper>
      <MenuText $isOpen={isOpen}>{label}</MenuText>
      {badge}
    </StyledNavLink>
  )
}

interface MenuBadgeProps {
  count?: number
}

export const MenuBadge = ({ count }: MenuBadgeProps) => {
  const { isOpen } = useVerticalMenuContext()

  if (!count) return null

  return <RedDot $isOpen={isOpen}>{count}</RedDot>
}

export { MenuIcon, MenuIconStroke, MenuItems, MenuText, RedDot, StyledNavLink } from "./styled"
